import * as profileEvents from "../../store/events/profile";
import Box from "@mui/material/Box";
import ErrorMessages from "../../components/ErrorMessages/ErrorMessages";
import Grid from "@mui/material/Grid";
import PendingInbox from "../../components/Loading/PendingInbox";
import React from "react";
import TaskHeader from "../../components/Task/TaskHeader";
import TaskTemplate from "../templates/TaskTemplate";
import Typography from "@mui/material/Typography";
import { connect } from "react-redux";
import { getErrorMessageForCode } from "../../config/common";
import { Link, useParams } from "react-router-dom";
import {
	format,
	isArray,
	isArrayOfLength,
	isObject,
	isStringOfLength
} from "@pheaa/channels-component-library";
import { styled, useTheme } from "@mui/material/styles";

const GridRow = styled(Grid)(({ theme }) => ({
	borderBottom: `1px solid ${theme.palette.neutral.main}`,
	minHeight: 72,
	paddingBottom: "0.4rem",
	paddingTop: "0.4rem",
	":last-of-type": {
		borderBottom: `1px solid ${theme.palette.neutral.main}`
	}
}));

const GridRowItem = styled(Grid)(({ theme }) => ({
	alignItems: "center",
	display: "flex",
	":last-child": {
		justifyContent: "flex-end"
	},
	minHeight: 72
}));

const GridRowItemDesc = props => {
	return <GridRowItem item xs={9} fontSize={12} {...props} />;
};

const GridRowItemLabel = props => {
	return <GridRowItem item xs={9} {...props} />;
};

const GridRowItemValue = props => {
	return <GridRowItem fontWeight={700} justifyContent="flex-end" textAlign="right" item xs={3} {...props} />;
};

const TaxSummaryView = ({ title, ...props }) => {

	let taskContent = null;
	let taxSummaryObj = null;

	const {
		getTaxSummaryById,
		inboxTaxSummaries,
		profileData
	} = props;

	const { taxSummaryId } = useParams();
	const theme = useTheme();

	React.useEffect(() => {
		const objExistsInStore = isObject(inboxTaxSummaries[taxSummaryId]);
		const objIsLoading = objExistsInStore && inboxTaxSummaries[taxSummaryId].isLoading;
		const objErrors = objExistsInStore && isArrayOfLength(inboxTaxSummaries[taxSummaryId].errors);
		const objSummaryId = objExistsInStore && isStringOfLength(inboxTaxSummaries[taxSummaryId].summaryId);

		if (!objIsLoading && !objErrors && !objSummaryId) {
			getTaxSummaryById(taxSummaryId);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	if (isObject(inboxTaxSummaries[taxSummaryId])) {
		taxSummaryObj = inboxTaxSummaries[taxSummaryId];

		if (taxSummaryObj.isLoading) {
			taskContent = <PendingInbox text={"Loading Tax Summary Statement..."} />;
		} else if (isStringOfLength(taxSummaryObj.summaryType)) {

			if (taxSummaryObj.summaryType === "1098e") {

				taskContent = (
					<React.Fragment>
						<Typography component="div" mb={4} sx={{ textAlign: "center" }}>
							Student Loan Interest for:
							<Typography mt={0.5} sx={{ fontWeight: 700 }}>
								{format.asProperName(profileData.demographics.name)}
							</Typography>
						</Typography>
						<GridRow container sx={{ borderTop: `1px solid ${theme.palette.neutral.main}` }}>
							<GridRowItemLabel>Tax Year</GridRowItemLabel>
							<GridRowItemValue>{taxSummaryObj.taxYear}</GridRowItemValue>
						</GridRow>
						<GridRow container>
							<GridRowItemLabel>Interest Paid</GridRowItemLabel>
							<GridRowItemValue>{format.asCurrency(taxSummaryObj.interestReported)}</GridRowItemValue>
							<GridRowItemDesc>
								This amount does {taxSummaryObj.includesOriginationFee ? null : "not"} include payments you made toward loan origination fees and/or capitalized interest on loans made before September 1, 2004.
							</GridRowItemDesc>
						</GridRow>
						<GridRow container>
							<GridRowItemLabel>PHEAA's Federal Identification Number, or EIN</GridRowItemLabel>
							<GridRowItemValue>23-1693326</GridRowItemValue>
						</GridRow>
						<Typography mt={4} paragraph={true} gutterBottom={true}>
							Additional Information
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							You may be able to deduct some or all of this interest on your federal income tax return.
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							Since the amount you paid toward interest does not exceed IRS reporting thresholds, we provide this notice in lieu of a 1098-E. This notice should be considered acceptable for use by your tax preparer.
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							If interest paid equals $0.00, you did not make any payments to us toward interest on these loans this tax year. This means that you either did not make any eligible payments or the payments you made reduced your principal balance rather than interest.
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							Consult a tax advisor or the IRS at 1-800-829-1040 or <a href="https://irs.gov" target="_blank" rel="noreferrer">IRS.gov</a> if you have questions about whether these payments are deductible on your tax return. <Link to="?help=phone">Contact us</Link> if you have questions about your payments.
						</Typography>
					</React.Fragment>
				);
			} else if (taxSummaryObj.summaryType === "1099c") {

				taskContent = (
					<React.Fragment>
						<Typography mb={4} sx={{ textAlign: "center" }}>
							Cancellation of Debt for:
							<Typography mt={0.5} sx={{ fontWeight: 700 }}>
								{format.asProperName(profileData.demographics.name)}
							</Typography>
						</Typography>
						<GridRow container sx={{ borderTop: `1px solid ${theme.palette.neutral.main}` }}>
							<GridRowItemLabel>Tax Year</GridRowItemLabel>
							<GridRowItemValue>{taxSummaryObj.taxYear}</GridRowItemValue>
						</GridRow>
						<GridRow container>
							<GridRowItemLabel>Total Canceled Debt<br />Including Interest</GridRowItemLabel>
							<GridRowItemValue>{format.asCurrency(taxSummaryObj.principalReported)}</GridRowItemValue>
						</GridRow>
						<GridRow container>
							<GridRowItemLabel>Interest Canceled</GridRowItemLabel>
							<GridRowItemValue>{format.asCurrency(taxSummaryObj.interestReported)}</GridRowItemValue>
						</GridRow>
						<GridRow container>
							<GridRowItemLabel>PHEAA's Federal Identification Number, or EIN</GridRowItemLabel>
							<GridRowItemValue>23-1693326</GridRowItemValue>
						</GridRow>
						<Typography mt={4} paragraph={true} gutterBottom={true}>
							Additional Information
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							This cancellation of debt may be considered as reportable income for tax purposes.
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							Since the amount of cancelled debt does not exceed IRS reporting thresholds, we provide this notice in lieu of a 1099-C. This notice should be considered acceptable for use by your tax preparer.
						</Typography>
						<Typography paragraph={true} gutterBottom={true}>
							Consult a tax advisor or the IRS at 1-800-829-1040 or <a href="https://irs.gov" target="_blank" rel="noreferrer">IRS.gov</a> if you have questions about how this information may be included on your tax return. <Link to="?help=phone">Contact us</Link> if you have questions about your payments.
						</Typography>
					</React.Fragment>
				);
			}
		}
	}

	return (
		<TaskTemplate>
			<TaskHeader
				closeTaskDestination={-1}
				title={title}
			/>
			<Box mt={6} mx="auto" sx={{ maxWidth: 450 }}>
				{
					isObject(taxSummaryObj) && isArray(taxSummaryObj.errors) && (
						<ErrorMessages
							errorMessages={taxSummaryObj.errors.map(error => {
								return { ...error, message: getErrorMessageForCode(error.code) };
							})}
						/>
					)
				}
				<Box mt={2.5}>
					{taskContent}
				</Box>
			</Box>
		</TaskTemplate>
	);
};

const mapStateToProps = state => ({
	inboxTaxSummaries: state.profile.inboxTaxSummaries,
	profileData: state.profile.data
});

const mapDispatchToProps = dispatch => ({
	getTaxSummaryById: data => dispatch(profileEvents.getTaxSummaryById(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(TaxSummaryView);